/* Importar documentos por carpetas: de la carpeta "Clientes" que un estudio
 * tiene en su computador o en Google Drive, a los casos de Tabularis.
 *
 * Es el cuello de botella de quien llega: años de documentos ordenados en
 * carpetas con el nombre de cada cliente, y subirlos caso por caso no lo hace
 * nadie. Acá se arrastra la carpeta entera (o los .zip que entrega Drive),
 * cada carpeta se cruza con su caso, lo que no tiene caso se crea, y el
 * abogado revisa antes de que se suba nada.
 *
 * La regla de oro es no equivocarse de caso: un documento en el caso de otro
 * cliente es peor que un caso duplicado. Por eso el cruce es por reglas
 * exactas y en este orden de certeza:
 *
 *   1. "carpeta"  la carpeta ya se importó antes a ese caso (queda anotado en
 *                 el caso: data.carpetaOrigen / data.codigoOrigen). Hace que
 *                 volver a importar no duplique nada.
 *   2. "codigo"   la carpeta lleva el código del caso (2026-0127, o el del
 *                 sistema anterior, SV-2026-7974). Exacto.
 *   3. "nombre"   el nombre del cliente coincide en dos o más palabras y con
 *                 un solo caso. Probable: se marca para mirar.
 *   4. "nuevo"    no se parece a ningún caso: se propone crearlo.
 *   5. "revisar"  hay duda (dos casos igual de parecidos, o una carpeta con
 *                 código que no existe pero con un cliente de nombre
 *                 parecido): no se decide solo.
 *
 * Sin IA a propósito: una regla exacta se puede revisar y da siempre lo
 * mismo. La pantalla está en components/Panel/ImportarDocumentos.tsx; esto se
 * prueba solo en importarCarpetas.test.ts.
 */
import { leerZip } from './zip';

/** Un archivo por subir, venga del disco o de dentro de un .zip. Se lee
 *  recién al subirlo: así un .zip de 2 GB no se carga entero en memoria. */
export type Fuente = { name: string; size: number; leer: () => Promise<Blob> };
export type ArchivoConRuta = { ruta: string; archivo: Fuente };

export const deArchivo = (f: File): Fuente => ({ name: f.name, size: f.size, leer: async () => f });

/** Lo mínimo de un caso para cruzarlo con una carpeta. */
export type CasoParaCruce = {
  id: string; nombreCliente?: string; rut?: string; codigoCaso?: string;
  /** Código del sistema anterior del estudio (SV-2026-7974). */
  codigoOrigen?: string;
  /** Carpeta de la que se creó o importó el caso, si vino de una importación. */
  carpetaOrigen?: string;
};

export const NUEVO = '__nuevo__';

export type Certeza = 'carpeta' | 'codigo' | 'nombre' | 'nuevo' | 'revisar';
export type Coincidencia = { casoId: string; puntos: number };

export type Grupo = {
  /** Identifica el grupo: carpeta del cliente + código, si hay. */
  clave: string;
  /** Carpeta del cliente, tal cual. */
  carpeta: string;
  /** Subcarpeta con el código del caso ("SV-2026-7974"), si la hay. */
  codigo: string | null;
  archivos: { archivo: Fuente; nombre: string }[];
  bytes: number;
  /** Casos posibles, del más al menos parecido. */
  candidatos: Coincidencia[];
  /** casoId, NUEVO, o null si lo tiene que decidir el abogado. */
  propuesto: string | null;
  certeza: Certeza;
};

/* ── Basura que no se importa ─────────────────────────────────────────── */

/** Accesos directos que deja Google Drive para escritorio en lugar de los Docs,
 *  Sheets y Slides: no son el documento, son un enlace de unos bytes. */
const EXT_GOOGLE = /\.(gdoc|gsheet|gslides|gdraw|gform|gmap|gsite|gjam|gscript)$/i;

/** Archivos del sistema y temporales que aparecen en cualquier carpeta. */
export function esBasura(ruta: string, bytes: number): boolean {
  if (/(^|\/)__MACOSX\//.test(ruta)) return true; // lo que agrega el .zip de un Mac
  const n = (ruta.split('/').pop() || '').trim();
  if (!n || bytes === 0) return true;
  if (n.startsWith('.') || n.startsWith('~$') || n.startsWith('._')) return true; // .DS_Store, ~$escrito.docx
  if (/^(desktop\.ini|thumbs\.db|icon\r?)$/i.test(n)) return true;
  if (/\.(lnk|tmp|crdownload|part|url|webloc)$/i.test(n)) return true;
  return false;
}
export const esAccesoDeGoogle = (ruta: string) => EXT_GOOGLE.test(ruta);

/* ── Leer lo que se arrastró ─────────────────────────────────────────── */

const esZip = (nombre: string) => /\.zip$/i.test(nombre);

/** Abre los .zip que se arrastraron o eligieron SUELTOS (los que entrega
 *  Drive). Un .zip guardado dentro de la carpeta de un cliente es un
 *  documento más y se sube tal cual. */
async function abrirZips(lista: { ruta: string; archivo: File }[]): Promise<ArchivoConRuta[]> {
  const salida: ArchivoConRuta[] = [];
  for (const x0 of lista) {
    // Una sola forma para las tildes y la ñ: el Mac las escribe a veces
    // "descompuestas" (n + ~), y la misma carpeta parecería otra.
    const x = { ...x0, ruta: x0.ruta.normalize('NFC') };
    if (!x.ruta.includes('/') && esZip(x.ruta)) {
      for (const e of await leerZip(x.archivo)) {
        salida.push({ ruta: e.ruta, archivo: { name: e.ruta.split('/').pop() || e.ruta, size: e.tamano, leer: e.leer } });
      }
    } else salida.push({ ruta: x.ruta, archivo: deArchivo(x.archivo) });
  }
  return salida;
}

/** Lee carpetas y .zip arrastrados al navegador, con todo lo que tienen
 *  adentro. `readEntries` entrega de a 100: hay que llamarlo hasta que vuelva
 *  vacío, o una carpeta grande se importa a medias sin aviso. */
export async function leerArrastre(items: DataTransferItemList): Promise<ArchivoConRuta[]> {
  const entradas: any[] = [];
  for (const it of Array.from(items)) {
    const e = (it as any).webkitGetAsEntry?.();
    if (e) entradas.push(e);
  }
  const salida: { ruta: string; archivo: File }[] = [];
  const recorrer = async (entrada: any, base: string): Promise<void> => {
    if (entrada.isFile) {
      const archivo: File = await new Promise((ok, mal) => entrada.file(ok, mal));
      salida.push({ archivo, ruta: base + archivo.name });
      return;
    }
    if (!entrada.isDirectory) return;
    const lector = entrada.createReader();
    for (;;) {
      const lote: any[] = await new Promise((ok, mal) => lector.readEntries(ok, mal));
      if (!lote.length) break;
      for (const hijo of lote) await recorrer(hijo, `${base}${entrada.name}/`);
    }
  };
  for (const e of entradas) await recorrer(e, '');
  return abrirZips(salida);
}

/** Lo mismo desde los botones "Elegir carpeta" y "Elegir .zip". */
export function leerSeleccion(files: FileList | File[]): Promise<ArchivoConRuta[]> {
  return abrirZips(Array.from(files).map(archivo => ({ archivo, ruta: (archivo as any).webkitRelativePath || archivo.name })));
}

/* ── Nombres, RUT y códigos ──────────────────────────────────────────── */

/** Palabras que no distinguen a nadie: "Juan Pérez de la Fuente" y "Carpeta
 *  cliente Juan Pérez" tienen que dar las mismas palabras útiles. */
const VACIAS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'con', 'contra', 'vs', 'v', 'en', 'por', 'para',
  'sr', 'sra', 'srta', 'don', 'dona', 'cliente', 'clienta', 'caso', 'causa', 'carpeta', 'docs', 'documentos',
  'spa', 'ltda', 'limitada', 'sa', 'eirl',
]);

export const normalizar = (s: string) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9ñ]+/g, ' ').trim();

/** Palabras de un nombre que sirven para reconocerlo: sin tildes, sin
 *  palabras vacías, sin números ni letras sueltas. */
export function palabras(s: string): Set<string> {
  return new Set(normalizar(s).split(' ').filter(p => p.length > 1 && !VACIAS.has(p) && !/^\d+$/.test(p)));
}

/** RUT sin puntos ni guion, en minúscula: "12.345.678-K" → "12345678k". */
export const rutLimpio = (s: string) => (s || '').replace(/[^0-9kK]/g, '').toLowerCase();
const RUT_EN_TEXTO = /\b\d{1,2}\.?\d{3}\.?\d{3}\s*-\s*[\dkK]\b/;

/** ¿El nombre de la carpeta es un código de caso? "SV-2026-7974",
 *  "2026-0127", "C 2025 112". Letras opcionales y dos grupos de números. */
export const esCodigo = (s: string) => /^[A-Za-z]{0,5}[-_ ]?\d{1,6}[-_ ]\d{2,6}$/.test(s.trim());
/** Un código comparable: mayúsculas y guiones. "sv 2026_7974" → "SV-2026-7974". */
export const codigoLimpio = (s: string) =>
  (s || '').trim().toUpperCase().replace(/[\s_]+/g, '-').replace(/^([A-Z]+)(\d)/, '$1-$2');

/** El nombre con que se crea el caso: sin el RUT si venía en la carpeta, y en
 *  mayúscula inicial si estaba todo en mayúsculas ("CESAR SALAZAR MUÑOZ" →
 *  "Cesar Salazar Muñoz"). */
export function nombreDeCliente(carpeta: string): { nombre: string; rut: string | null } {
  const m = carpeta.match(RUT_EN_TEXTO);
  let nombre = (m ? carpeta.replace(m[0], ' ') : carpeta).replace(/\s+/g, ' ').replace(/^[\s\-–·,]+|[\s\-–·,]+$/g, '').trim();
  if (nombre && nombre === nombre.toUpperCase() && /[A-ZÑÁÉÍÓÚ]/.test(nombre)) {
    nombre = nombre.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, a, b) => a + b.toUpperCase());
  }
  return { nombre: nombre || carpeta, rut: m ? m[0].replace(/\s/g, '') : null };
}

/* ── Cruce de una carpeta con los casos ─────────────────────────────── */

/** Qué tan parecido es el nombre de la carpeta al cliente del caso.
 *  100  el código del caso o el RUT del cliente están en el nombre
 *  ≥2   palabras del nombre en común (+0,5 si coincide el nombre completo)
 *    0  menos de dos palabras en común: "Pérez" solo no alcanza. */
export function puntaje(carpeta: string, caso: CasoParaCruce): number {
  const texto = ` ${normalizar(carpeta)} `;
  for (const cod of [caso.codigoCaso, caso.codigoOrigen]) {
    if (cod && texto.includes(` ${normalizar(cod)} `)) return 100;
  }
  const rut = rutLimpio(caso.rut || '');
  const enCarpeta = carpeta.match(RUT_EN_TEXTO);
  if (rut.length >= 7 && enCarpeta && rutLimpio(enCarpeta[0]) === rut) return 100;
  const a = palabras(carpeta);
  const b = palabras(caso.nombreCliente || '');
  let comunes = 0;
  a.forEach(p => { if (b.has(p)) comunes++; });
  if (comunes < 2) return 0;
  return comunes + (comunes === b.size ? 0.5 : 0);
}

export function cruzar(carpeta: string, codigo: string | null, casos: CasoParaCruce[]): Pick<Grupo, 'candidatos' | 'propuesto' | 'certeza'> {
  const cod = codigoLimpio(codigo || '');
  // 1. Ya se importó antes desde esta misma carpeta (y el mismo código).
  const previo = casos.find(c => c.carpetaOrigen === carpeta && codigoLimpio(c.codigoOrigen || '') === cod);
  if (previo) return { candidatos: [{ casoId: previo.id, puntos: 1000 }], propuesto: previo.id, certeza: 'carpeta' };
  // 2. Por código exacto.
  if (cod) {
    const exactos = casos.filter(c => codigoLimpio(c.codigoCaso || '') === cod || codigoLimpio(c.codigoOrigen || '') === cod);
    if (exactos.length === 1) return { candidatos: [{ casoId: exactos[0].id, puntos: 1000 }], propuesto: exactos[0].id, certeza: 'codigo' };
  }
  // 3. Por nombre (o por un código o RUT escrito en el nombre de la carpeta).
  const candidatos = casos
    .map(c => ({ casoId: c.id, puntos: puntaje(carpeta, c) }))
    .filter(c => c.puntos > 0)
    .sort((x, y) => y.puntos - x.puntos);
  if (!candidatos.length) return { candidatos, propuesto: NUEVO, certeza: 'nuevo' };
  // Una carpeta con código que no existe en Tabularis, de un cliente que sí
  // está: puede ser otra causa del mismo cliente. No se adivina.
  if (cod && candidatos[0].puntos < 100) return { candidatos, propuesto: null, certeza: 'revisar' };
  const empate = candidatos.length > 1 && candidatos[0].puntos === candidatos[1].puntos;
  if (empate) return { candidatos, propuesto: null, certeza: 'revisar' };
  return { candidatos, propuesto: candidatos[0].casoId, certeza: candidatos[0].puntos >= 100 ? 'codigo' : 'nombre' };
}

/* ── Agrupar por cliente y por caso ─────────────────────────────────── */

const segmentos = (ruta: string) => ruta.split('/').filter(Boolean);

/** Nombre con que queda el documento en el caso: el archivo, precedido de las
 *  subcarpetas que había adentro, para no perder ese orden
 *  ("Liquidaciones - enero.pdf"). */
function nombreDentroDelCaso(partes: string[]): string {
  const n = partes.join(' - ');
  if (n.length <= 255) return n;
  const archivo = partes[partes.length - 1];
  return archivo.length <= 255 ? archivo : archivo.slice(-255);
}

/** Cuántas carpetas comparten TODOS los archivos desde arriba ("Clientes/"). */
export function prefijoComun(lista: ArchivoConRuta[]): number {
  const rutas = lista.map(x => segmentos(x.ruta));
  if (!rutas.length) return 0;
  for (let comun = 0; ; comun++) {
    const seg = rutas[0][comun];
    for (const r of rutas) if (r.length <= comun + 1 || r[comun] !== seg) return comun;
  }
}

/** Agrupa los archivos en casos. Los clientes son las carpetas que están a
 *  `profundidad` (0 = la de más arriba de lo arrastrado). Si la carpeta de un
 *  cliente tiene subcarpetas con código de caso ("SV-2026-7974"), cada una es
 *  un caso aparte; los archivos sueltos del cliente van a ese caso si tiene
 *  uno solo, y si tiene varios, se pregunta. Lo que está por encima del nivel
 *  de los clientes no tiene a quién ir y queda en `sueltos`. */
export function agrupar(lista: ArchivoConRuta[], casos: CasoParaCruce[], profundidad: number) {
  const porCliente = new Map<string, Map<string, { archivos: Grupo['archivos']; bytes: number }>>();
  const sueltos: ArchivoConRuta[] = [];
  for (const x of lista) {
    const r = segmentos(x.ruta);
    if (r.length <= profundidad + 1) { sueltos.push(x); continue; }
    const carpeta = r[profundidad];
    const resto = r.slice(profundidad + 1);
    const conCodigo = resto.length >= 2 && esCodigo(resto[0]);
    const codigo = conCodigo ? resto[0].trim() : '';
    let cliente = porCliente.get(carpeta);
    if (!cliente) { cliente = new Map(); porCliente.set(carpeta, cliente); }
    let g = cliente.get(codigo);
    if (!g) { g = { archivos: [], bytes: 0 }; cliente.set(codigo, g); }
    g.archivos.push({ archivo: x.archivo, nombre: nombreDentroDelCaso(conCodigo ? resto.slice(1) : resto) });
    g.bytes += x.archivo.size;
  }

  const grupos: Grupo[] = [];
  porCliente.forEach((porCodigo, carpeta) => {
    const codigos = Array.from(porCodigo.keys()).filter(Boolean);
    // Un cliente con UNA carpeta de caso: lo suelto también es de ese caso.
    if (codigos.length === 1 && porCodigo.has('')) {
      const suelto = porCodigo.get('')!, unico = porCodigo.get(codigos[0])!;
      unico.archivos.push(...suelto.archivos);
      unico.bytes += suelto.bytes;
      porCodigo.delete('');
    }
    porCodigo.forEach((g, codigo) => {
      const cruce = cruzar(carpeta, codigo || null, casos);
      // Lo suelto de un cliente con varias causas no se reparte solo.
      const dudoso = !codigo && codigos.length > 1 && cruce.certeza !== 'carpeta';
      grupos.push({
        clave: `${carpeta}|${codigo}`, carpeta, codigo: codigo || null, archivos: g.archivos, bytes: g.bytes,
        ...cruce,
        ...(dudoso ? { propuesto: null, certeza: 'revisar' as Certeza } : {}),
      });
    });
  });
  grupos.sort((a, b) => a.carpeta.localeCompare(b.carpeta, 'es') || (a.codigo || '').localeCompare(b.codigo || ''));
  return { grupos, sueltos };
}

/** Los niveles que tiene sentido ofrecer: desde uno antes del primer lugar
 *  donde se separan las rutas (por si se arrastró la carpeta de UN cliente)
 *  hasta dos más abajo ("Clientes/2024/Juan Pérez"). Un nivel donde las
 *  carpetas son códigos de caso no es el de los clientes: es el de abajo. */
export function nivelesPosibles(lista: ArchivoConRuta[]): number[] {
  const comun = prefijoComun(lista);
  const niveles: number[] = [];
  for (let d = Math.max(0, comun - 1); d <= comun + 2; d++) {
    const nombres = new Set<string>();
    for (const x of lista) { const r = segmentos(x.ruta); if (r.length > d + 1) nombres.add(r[d]); }
    if (!nombres.size) continue;
    const codigos = Array.from(nombres).filter(esCodigo).length;
    if (codigos * 2 > nombres.size) continue;
    niveles.push(d);
  }
  return niveles;
}

/** El nivel de los clientes: el que reconoce más casos. Si no reconoce
 *  ninguno —un estudio que recién llega—, el primero donde las carpetas se
 *  separan. */
export function mejorNivel(lista: ArchivoConRuta[], casos: CasoParaCruce[]): number {
  const comun = prefijoComun(lista);
  const niveles = nivelesPosibles(lista);
  // Con una sola carpeta de cliente, todo comparte hasta su nombre y el nivel
  // "donde se separan" ya son los archivos: ahí el cliente es el anterior.
  let mejor = niveles.includes(comun) ? comun : (niveles[0] ?? 0), maximo = 0;
  for (const d of niveles) {
    const reconocidos = agrupar(lista, casos, d).grupos.filter(g => g.certeza !== 'nuevo').length;
    if (reconocidos > maximo) { maximo = reconocidos; mejor = d; }
  }
  return mejor;
}

/** Separa lo importable de lo que no, con la ruta de cada descarte. */
export function filtrar(lista: ArchivoConRuta[]) {
  const utiles: ArchivoConRuta[] = [];
  const basura: string[] = [];
  const deGoogle: string[] = [];
  for (const x of lista) {
    if (esAccesoDeGoogle(x.ruta)) deGoogle.push(x.ruta);
    else if (esBasura(x.ruta, x.archivo.size)) basura.push(x.ruta);
    else utiles.push(x);
  }
  return { utiles, basura, deGoogle };
}

/** Tipo de archivo por la extensión: uno sacado de un .zip no lo trae, y sin
 *  él la vista previa y la descarga no saben qué es. */
export function tipoPorNombre(nombre: string): string {
  const e = (nombre.toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';
  return ({
    pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
    heic: 'image/heic', tif: 'image/tiff', tiff: 'image/tiff', bmp: 'image/bmp',
    doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain', csv: 'text/csv', rtf: 'application/rtf', odt: 'application/vnd.oasis.opendocument.text',
    eml: 'message/rfc822', msg: 'application/vnd.ms-outlook', mp3: 'audio/mpeg', m4a: 'audio/mp4', mp4: 'video/mp4', zip: 'application/zip',
  } as Record<string, string>)[e] || 'application/octet-stream';
}

export const fmtBytes = (b: number) =>
  b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB`
    : b < 1024 ** 3 ? `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
      : `${(b / 1024 ** 3).toFixed(2).replace('.', ',')} GB`;
